import React from 'react';
import { TaskyBadge, TaskyCard } from '../tasky';
import {
  BANNER_AB_ROUND_STATUS_LABELS,
  type BannerAbImageSummary,
  type BannerAbTestComparison,
  type BannerAbTestRound,
} from '../../types/bannerAbTest';

export interface RoundTimelineProps {
  rounds: BannerAbTestRound[];
  comparisons: BannerAbTestComparison[];
  imagesById: Record<number, BannerAbImageSummary>;
}

const Thumb: React.FC<{ image?: BannerAbImageSummary; label: string; highlighted?: boolean }> = ({ image, label, highlighted }) => (
  <div className={`rounded-md border p-1 ${highlighted ? 'border-primary' : 'border-warm-border'}`}>
    {image ? (
      <img src={image.thumbnail_url || image.url} alt={label} className="h-20 w-28 rounded bg-warm-surface object-contain" />
    ) : (
      <div className="h-20 w-28 rounded bg-warm-surface" />
    )}
    <div className="mt-1 text-center text-xs text-gray-500">{label}</div>
  </div>
);

const roundVariant = (status: BannerAbTestRound['status']) =>
  status === 'failed' ? 'destructive' : status === 'completed' ? 'success' : 'info';

/** 自動改善ループのラウンドごとの経過。仮説 → 派生案 → 勝敗 → 王者交代の有無を1枚で追える */
export const RoundTimeline: React.FC<RoundTimelineProps> = ({ rounds, comparisons, imagesById }) => (
  <div className="space-y-4">
    {rounds.map((round) => {
      const challengerIds = round.challenger_image_ids ?? [];
      const inRound = comparisons.filter(
        (c) => c.image_a_id === round.champion_image_id && challengerIds.includes(c.image_b_id),
      );

      return (
        <TaskyCard key={round.id} variant="bordered" padding="md" className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-dark">ラウンド {round.round_no}</h3>
            <TaskyBadge variant={roundVariant(round.status)}>
              {BANNER_AB_ROUND_STATUS_LABELS[round.status] ?? round.status}
            </TaskyBadge>
            {/* 交代の有無は決着してからでないと言えないので、completed 以外は黙る */}
            {round.status === 'completed' && (
              <span className="text-xs text-gray-500">
                {round.improved ? '王者が入れ替わりました' : '王者は据え置きです'}
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <Thumb
              image={round.champion_image_id != null ? imagesById[round.champion_image_id] : undefined}
              label={`王者 #${round.champion_image_id ?? '-'}`}
            />
            {challengerIds.length > 0 && <span className="pb-6 text-gray-400">→</span>}
            {challengerIds.map((id) => {
              const comparison = inRound.find((c) => c.image_b_id === id);
              // 印の正は round.winner_image_id。verdict='b' で判定すると、王者を上回った派生案が
              // 2枚以上あるラウンドで両方に印が付く（新王者になるのは delta 最大の1枚だけ）
              const won = round.improved && round.winner_image_id === id;
              const delta = comparison?.delta;
              return (
                <div key={id} {...(won ? { 'data-testid': `round-${round.round_no}-winner` } : {})}>
                  <Thumb
                    image={imagesById[id]}
                    label={`#${id}${delta == null ? '' : `（差 ${delta.toFixed(2)}）`}`}
                    highlighted={won}
                  />
                </div>
              );
            })}
          </div>

          {(round.hypotheses ?? []).length > 0 && (
            <div>
              <div className="mb-1 text-xs font-semibold text-gray-500">立てた改善仮説</div>
              <ul className="list-disc space-y-0.5 pl-5 text-sm text-dark">
                {(round.hypotheses ?? []).map((h, i) => (
                  <li key={`${round.id}-${i}`}>{h.instruction}</li>
                ))}
              </ul>
            </div>
          )}
        </TaskyCard>
      );
    })}
  </div>
);

export default RoundTimeline;
